import React, { PureComponent } from 'react';
import { TextField, Popper, Grid, Paper, IconButton, Button, Typography, Box } from '@mui/material';
import MapButton from './MapButton';
import { IoMdOptions } from 'react-icons/io';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '../../utils';
import { MAP_TYPES, MapType } from '../helpers/mapTypes';
import EventBus from '../../../EventBus';
import { dispatchMissionUpdated, MISSION_EVENTS } from '../../../missionEvents';
import { dispatchCoresNeedUpdate } from '../../../mapEvents';
import { getCurrentMission } from '../../../dataModelHelpers';
import { LocalStorageGenerator, updateState } from '../../../utils';
import { SettingsAccessLevel } from '../../../types/types';
import { alertSuccess } from '../../../alertDispatcher';
import { FEET_BETWEEN_CORE_LINES_FOR_MULTI_LAB } from '../../../constants';
import logger from '../../../logger';

interface SamplingParametersButtonProps {
  zoneMission: boolean;
  type: MapType;
  accessLevel: SettingsAccessLevel;
  addCheckpoint: (name: string) => void;
}

interface SamplingParametersButtonState {
  open: boolean;
  numCores: number;
  angle: number;
  length: number;
  anchorEl: HTMLElement | null;
  missionId: string;
  originalCores: number;
  line_separation_ft: number;
}

interface NumberOfCores {
  cores: number;
  missionId: string;
}

const coreNumberStorage = LocalStorageGenerator<NumberOfCores>('coreNumberStorage', { cores: 0, missionId: '' });

class SamplingParametersButton extends PureComponent<SamplingParametersButtonProps, SamplingParametersButtonState> {
  SERIALIZABLE_STATE_VARIABLES: (keyof SamplingParametersButtonState)[] = ['open'];
  shiftDown: boolean;
  ctrlDown: boolean;

  constructor(props: SamplingParametersButtonProps) {
    super(props);

    this.state = {
      numCores: 0,
      originalCores: coreNumberStorage.get().cores,
      missionId: coreNumberStorage.get().missionId,
      length: 0,
      open: false,
      anchorEl: null,
      angle: 0,
      line_separation_ft: FEET_BETWEEN_CORE_LINES_FOR_MULTI_LAB,
    };

    this.toggleOpen = this.toggleOpen.bind(this);
    this.getParameters = this.getParameters.bind(this);
    this.handleUpdateParameters = this.handleUpdateParameters.bind(this);
    this.shiftDown = false;
    this.ctrlDown = false;
    this.chooseMinMaxCores = this.chooseMinMaxCores.bind(this);
    this.chooseCanAdjustDownAndUp = this.chooseCanAdjustDownAndUp.bind(this);
  }

  componentDidMount() {
    EventBus.on(MISSION_EVENTS.UPDATED, this.getParameters);
    this.deserializeState();

    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);

    const anchorEl = document.getElementById('samplingParameterButton');
    this.setState({ anchorEl });
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.UPDATED, this.getParameters);
    this.serializeState();

    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      this.shiftDown = true;
    }

    if (event.key === 'Control') {
      this.ctrlDown = true;
    }
  };

  handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      this.shiftDown = false;
    }

    if (event.key === 'Control') {
      this.ctrlDown = false;
    }
  };

  componentDidUpdate(_prevProps: SamplingParametersButtonProps, prevState: SamplingParametersButtonState) {
    if (this.state.open && !prevState.open) {
      this.getParameters();
    }
  }

  /**
   * serialize the state into session
   */
  serializeState() {
    const serializedState = {};

    for (const stateKey of this.SERIALIZABLE_STATE_VARIABLES) {
      serializedState[stateKey] = this.state[stateKey];
    }

    localStorage.setItem('SamplingParametersButtonState', JSON.stringify(serializedState));
  }

  /**
   * deserialize the state
   */
  deserializeState() {
    const serializedState = JSON.parse(localStorage.getItem('SamplingParametersButtonState') || '{}');

    if (serializedState) {
      for (const stateKey of Object.keys(serializedState)) {
        this.setState(updateState(stateKey as keyof SamplingParametersButtonState, serializedState[stateKey]));
      }
    }
  }

  getParameters() {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    const spec = mission.getPrimarySpec();
    const isBdMission = mission.isBDMission();

    let originalCores = this.state.originalCores;

    if (this.state.missionId !== mission.job_id) {
      originalCores = spec.cores || (isBdMission ? 1 : 0);
      coreNumberStorage.set({ cores: originalCores, missionId: mission.job_id ?? '' });
    }

    this.setState({
      numCores: spec.cores,
      length: spec.length,
      missionId: mission.job_id ?? '',
      originalCores,
      angle: spec.angle,
      line_separation_ft: spec.parallel_line_distance_ft,
    });
  }

  toggleOpen(_: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    this.setState({ open: !this.state.open });
  }

  async handleUpdateParameters() {
    try {
      this.props.addCheckpoint('Update Path');
      const mission = getCurrentMission();

      if (!mission) {
        return;
      }

      const spec = mission.getPrimarySpec();

      const newParameters = {
        cores: this.state.numCores,
        length: this.state.length,
        angle: this.state.angle,
        parallel_line_distance_ft: this.state.line_separation_ft,
      };
      await spec.update_parameters(newParameters);

      await logger.log(
        'SamplingParametersButton - handleUpdateParameters - calculate_path_restore_cores',
        newParameters,
      );
      await mission.calculate_path_restore_cores();

      alertSuccess('Updated sampling parameters');
    } catch (err) {
      console.error('Could not update parameters', err);
    }

    dispatchMissionUpdated();
    // calculate_path_restore_cores → calculate_path already fires
    // dispatchCoresNeedUpdate() internally.
  }

  chooseMinMaxCores(): [number, number] {
    const mission = getCurrentMission();

    if (!mission) {
      return [0, 0];
    }

    const isBdMission = mission.isBDMission();
    const spec = mission.getPrimarySpec();

    if (isBdMission) {
      return [spec.cores || 1, spec.cores || 1];
    }

    const isTechnician = this.props.accessLevel === 'Technician';
    const isOperatorTuning = this.props.accessLevel === 'Operator Tuning';

    // We allow technicians and operator tuners to set min cores to original cores # - 1.
    const adjustedOriginalCoresNumber =
      isTechnician || isOperatorTuning ? this.state.originalCores - 1 : this.state.originalCores;

    const minCores = Math.max(adjustedOriginalCoresNumber, 6);
    const maxCores = minCores + 2;

    return [minCores, maxCores];
  }

  chooseCanAdjustDownAndUp(minCores: number, maxCores: number): [boolean, boolean] {
    const mission = getCurrentMission();

    if (!mission) {
      return [false, false];
    }

    const mapMaking = this.props.type === MAP_TYPES.MAPS;
    const isTechnician = this.props.accessLevel === 'Technician';

    // We allow technicians (and mapmakers) to go as low as 0.
    if (isTechnician || mapMaking) {
      return [this.state.numCores > 0, true];
    }

    const canAdjustDown = this.state.numCores > minCores && this.state.numCores > 0;
    const canAdjustUp = this.state.numCores < maxCores;

    return [canAdjustDown, canAdjustUp];
  }

  render() {
    const [minCores, maxCores] = this.chooseMinMaxCores();
    const [canAdjustDown, canAdjustUp] = this.chooseCanAdjustDownAndUp(minCores, maxCores);
    const cantAdjustDown = !canAdjustDown;
    const cantAdjustUp = !canAdjustUp;
    const atMinCores = this.state.numCores <= minCores;
    const atMaxCores = this.state.numCores >= maxCores;

    const specCount = getCurrentMission()?.spec_count;
    const multiSpec = specCount && specCount > 1;

    return (
      <React.Fragment>
        <MapButton
          tooltip="Sampling Parameters"
          toggled={Boolean(this.state.open)}
          onClick={this.toggleOpen}
          id={'samplingParameterButton'}
        >
          <IoMdOptions fontSize={18} />
        </MapButton>
        <Popper anchorEl={this.state.anchorEl} open={this.state.open} style={{ zIndex: 2, minWidth: 230 }}>
          <Paper style={{ padding: 10 }}>
            <IconButton
              onClick={() => this.setState({ open: false })}
              size={'small'}
              style={{ position: 'absolute', right: 5, color: 'black' }}
            >
              <CloseIcon fontSize={'medium'} />
            </IconButton>
            <Grid size={12}>
              {/* sx={{ border: '2px solid grey' }} */}
              <Box style={{ padding: 10, marginBottom: 10, borderColor: 'black', borderWidth: 2, borderRadius: 1 }}>
                <Typography variant={'body2'} style={{ marginBottom: 10 }}>
                  Cores
                </Typography>
                <Button
                  disabled={cantAdjustDown}
                  variant={'outlined'}
                  style={{ textTransform: 'none', marginTop: 1 }}
                  onClick={() => {
                    this.setState({ numCores: this.state.numCores - 1 });
                  }}
                >
                  -
                </Button>
                <TextField
                  value={this.state.numCores}
                  inputProps={{ readOnly: true }}
                  variant={'outlined'}
                  type={'number'}
                  size={'small'}
                  style={{ marginLeft: 5, marginRight: 5, maxWidth: 80, alignContent: 'center' }}
                />
                <Button
                  disabled={cantAdjustUp}
                  variant={'outlined'}
                  style={{ textTransform: 'none', marginTop: 1 }}
                  onClick={() => {
                    this.setState({ numCores: this.state.numCores + 1 });
                  }}
                >
                  +
                </Button>
              </Box>
            </Grid>
            <Grid size={12}>
              {/* sx={{ border: '2px solid grey' }} */}
              <Box style={{ padding: 10, marginBottom: 10, borderColor: 'black', borderWidth: 2, borderRadius: 1 }}>
                <Typography variant={'body2'} style={{ marginBottom: 10 }}>
                  Angle
                </Typography>
                <Button
                  disabled={cantAdjustDown}
                  variant={'outlined'}
                  style={{ textTransform: 'none', marginTop: 1 }}
                  onClick={() => {
                    this.setState({ angle: this.state.angle - (this.shiftDown ? 0.1 : this.ctrlDown ? 5 : 1) });
                  }}
                >
                  -
                </Button>
                <TextField
                  value={this.state.angle}
                  inputProps={{ readOnly: true }}
                  variant={'outlined'}
                  type={'number'}
                  size={'small'}
                  style={{ marginLeft: 5, marginRight: 5, maxWidth: 80, alignContent: 'center' }}
                />
                <Button
                  disabled={cantAdjustUp}
                  variant={'outlined'}
                  style={{ textTransform: 'none', marginTop: 1 }}
                  onClick={() => {
                    this.setState({ angle: this.state.angle + (this.shiftDown ? 0.1 : this.ctrlDown ? 5 : 1) });
                  }}
                >
                  +
                </Button>
              </Box>
            </Grid>
            <Grid size={12}>
              {atMinCores && <span style={{ marginLeft: 5, color: 'red' }}>{`Minimum cores: ${minCores}`}</span>}

              {atMaxCores && <span style={{ marginLeft: 5, color: 'red' }}>{`Maximum cores: ${maxCores}`}</span>}
            </Grid>
            {!this.props.zoneMission && (
              <Grid size={12}>
                <TextField
                  disabled={this.props.type === MAP_TYPES.SAMPLING && this.props.accessLevel === 'Locked'}
                  value={this.state.length}
                  variant={'outlined'}
                  label={'length'}
                  type={'number'}
                  size={'small'}
                  style={{ marginLeft: 10, marginRight: 40, marginTop: 10, maxWidth: 220 }}
                  onChange={(event) => this.setState({ length: parseInt(event.target.value || '') })}
                />
              </Grid>
            )}
            {multiSpec && (
              <Grid size={12}>
                <TextField
                  disabled={this.props.type === MAP_TYPES.SAMPLING && this.props.accessLevel === 'Locked'}
                  value={this.state.line_separation_ft}
                  variant={'outlined'}
                  label={'line distance'}
                  type={'number'}
                  size={'small'}
                  style={{ marginLeft: 10, marginRight: 40, marginTop: 10, maxWidth: 220 }}
                  onChange={(event) => this.setState({ line_separation_ft: parseInt(event.target.value.toString()) })}
                />
              </Grid>
            )}
            <Grid style={{ textAlign: 'right', marginRight: 10, marginTop: 10 }} size={12}>
              <LoadingButton
                style={{ textTransform: 'none' }}
                size={'medium'}
                color={'secondary'}
                variant={'contained'}
                onClick={this.handleUpdateParameters}
              >
                Update Path
              </LoadingButton>
            </Grid>
          </Paper>
        </Popper>
      </React.Fragment>
    );
  }
}

export default SamplingParametersButton;
